import React, { useContext } from 'react'
import { Dialog, Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { useWidth } from '../../../core/utils'
import { ModalContext } from '../contexts'
import { TYPES } from '../constants'
import { modalStyles } from './modal.styles'
import { withStyles } from '@mui/styles'

/**
 * @typedef {object} ModalComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 */

/**
 * Componente de modal global que renderiza diálogos dinámicos usando contexto.
 * 
 * Este componente se integra con ModalContext para mostrar diferentes tipos de diálogos:
 * - **FULL_SCREEN**: Modal a pantalla completa
 * - **RESPONSIVE**: Pantalla completa en móvil, modal normal en desktop
 * - **Normal**: Modal estándar con ancho máximo configurable
 * 
 * El componente renderiza dinámicamente el contenido pasado a través del contexto,
 * permitiendo mostrar cualquier componente React como modal. Incluye un botón de
 * cierre opcional y pasa la función `close` al componente hijo.
 * 
 * @component
 * @example
 * // Uso con el hook useModal
 * const { open } = useModal();
 * 
 * const handleOpenModal = () => {
 *   open(MyCustomComponent, { data: 'some data' }, 'md', true);
 * };
 * 
 * @param {ModalComponentProps} props - Las props del componente ModalComponent.
 */
export const ModalComponent = withStyles(modalStyles)(({ classes }) => {
  const {
    actions: { close },
    isOpen,
    type,
    Component,
    data,
    maxWidth,
    closable = false,
  } = useContext(ModalContext)

  const isMobileWidth = useWidth() === 'xs'

  return (
    <Dialog
      open={isOpen}
      fullScreen={type === TYPES.FULL_SCREEN || (type === TYPES.RESPONSIVE && isMobileWidth)}
      className={classes[type]}
      maxWidth={maxWidth}
      fullWidth
    >
      <Box style={{ position: 'relative' }}>
        {closable ? (
          <IconButton onClick={close} style={{ position: 'absolute', right: 0 }}>
            <CloseIcon />
          </IconButton>
        ) : null}
        {!!Component ? <Component close={close} {...data} /> : null}
      </Box>
    </Dialog>
  )
})
